import Progress from '@components/v2/ui/Progress/Progress';
import StatusChip from '@components/v2/ui/StatusChip/StatusChip';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { Fragment } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { ORDER_STATUS_CHIP_CONFIG } from '../../../orders/list/components/orderStatusChip.constants';
import { useTranslation } from '../../../translation/TranslationContext';
import useTargetConversionView from '../../hooks/useTargetConversionView';
import useTargetValueFormatter from '../../hooks/useTargetValueFormatter';
import { TARGET_EMPTY_VALUE } from '../../target.constants';
import { TargetEditability } from '../../targetStatus.rules';
import { useTargetConversionCellStyles } from './targetConversionCell.styles';
import { TargetConversionCellProps } from './targetConversionCell.types';

const TITLE_VARIANT = 'micro1';
const VALUE_VARIANT = 'body2';

const TargetConversionCell = ({
  attainment,
  unit,
  targetStatus,
  labelPosition,
  labelColor,
  labelVariant,
}: TargetConversionCellProps) => {
  const { translate } = useTranslation();
  const { classes } = useTargetConversionCellStyles();
  const { formatValue } = useTargetValueFormatter(unit);
  const { toConversionView } = useTargetConversionView();
  const { conversion, byStatus, updatedAt } = toConversionView({ ...attainment, formatValue });

  if (targetStatus && TargetEditability.isUnderConstruction(targetStatus)) {
    return TARGET_EMPTY_VALUE;
  }

  const ring = (
    <Progress
      value={conversion}
      labelPosition={labelPosition}
      labelColor={labelColor}
      labelVariant={labelVariant}
    />
  );

  if (byStatus.length === 0) {
    return ring;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Tooltip
        TooltipContent={
          <Box className={classes.content}>
            <Typography variant={TITLE_VARIANT}>{translate(LangEnum.GLOBAL_STATUS)}</Typography>
            <Typography variant={TITLE_VARIANT} className={classes.amount}>
              {translate(LangEnum.GLOBAL_ACHIEVED_VALUE)}
            </Typography>
            <Typography variant={TITLE_VARIANT} className={classes.amount}>
              {translate(LangEnum.GLOBAL_CONVERSION)}
            </Typography>
            {byStatus.map((item) => (
              <Fragment key={item.status ?? TARGET_EMPTY_VALUE}>
                <Box className={classes.status}>
                  {item.status ? (
                    <StatusChip {...ORDER_STATUS_CHIP_CONFIG[item.status]} size="small" />
                  ) : (
                    <Typography variant={VALUE_VARIANT}>{TARGET_EMPTY_VALUE}</Typography>
                  )}
                </Box>
                <Typography variant={VALUE_VARIANT} className={classes.amount}>
                  {item.value}
                </Typography>
                <Typography variant={VALUE_VARIANT} className={classes.amount}>
                  {item.conversion}
                </Typography>
              </Fragment>
            ))}
            {updatedAt && (
              <Typography variant={TITLE_VARIANT} className={classes.updatedAt}>
                {updatedAt}
              </Typography>
            )}
          </Box>
        }
      >
        {ring}
      </Tooltip>
    </Box>
  );
};

export default TargetConversionCell;
